import { EventEmitter } from '../utils/events.js';
import { PROVIDERS, fetchFromProvider } from './providers.js';
import { generateDemoFlights, radiusToBoundingBox } from './demoFlights.js';
import { fetchRoute } from './enrichment.js';

/**
 * Client-side polling service — replaces the former WebSocket server.
 *
 * Responsibilities:
 * - Poll ADS-B providers on an interval, with automatic failover
 * - Seed the position history from the optional server-side logger
 *   (/history — see server/README.md), so trails + timeline are filled
 *   immediately after opening the app
 * - Accumulate per-flight position history (for trails + timeline)
 * - Keep vanished flights around (flagged `gone`) until their history ages
 *   out — the timeline can still show them when rewinding
 * - Resolve flight routes in the background (adsb.lol route DB) and tag
 *   flights departing from / arriving at the home airport (flight.strTag)
 * - Fall back to demo flights after repeated total failures
 * - Pause polling while the tab is hidden
 *
 * Events:
 * - 'update'  → Array<flight> (each with .history attached; may include gone)
 * - 'status'  → { state: 'live'|'fallback'|'demo'|'error', provider, error }
 */
export class FlightDataService extends EventEmitter {
  constructor({ center, radiusKm, pollIntervalMs, historyDurationMs, staleAfterMs, airport = null }) {
    super();
    this.center = center;
    this.radiusKm = radiusKm;
    this.pollIntervalMs = pollIntervalMs;
    this.historyDurationMs = historyDurationMs;
    this.staleAfterMs = staleAfterMs;

    // Heimatflughafen (z. B. { code: 'STR', icao: 'EDDS' }) für die
    // Start-/Lande-Kennzeichnung der Flüge
    this.airport = airport;
    this.routeTags = new Map(); // callsign -> 'departure' | 'arrival'
    this._routeChecked = new Map(); // callsign -> Zeitstempel des letzten Versuchs

    this.tracked = new Map(); // id -> { flight, history, lastSeen }
    this.timer = null;
    this.consecutiveErrors = 0;
    this.lastFetchTime = 0;
    this.minFetchGapMs = 2000;
    this.polling = false;
    this.lastStatus = null;

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this._clearTimer();
      } else if (this.timer === null && this.started) {
        this.poll();
        this._startTimer();
      }
    });

    window.addEventListener('offline', () => {
      this._emitStatus({ state: 'error', provider: null, error: 'Offline' });
    });
    window.addEventListener('online', () => {
      if (this.started && !document.hidden) this.poll();
    });
  }

  start() {
    this.started = true;
    this.poll();
    this._startTimer();
  }

  stop() {
    this.started = false;
    this._clearTimer();
  }

  setRadius(radiusKm) {
    this.radiusKm = radiusKm;
    this.poll();
  }

  /**
   * Seed the tracked flights from the server-side history logger.
   * The endpoint returns the last ~30 minutes of positions for aircraft
   * around Stuttgart: { aircraft: [{ hex, callsign, t, category,
   * points: [[tsMs, lat, lon, altM], …] }] }.
   *
   * Fails silently (returns false) if the endpoint is not deployed —
   * the app then behaves exactly like before (history builds up live).
   */
  async seedHistory(url = '/history', timeoutMs = 10000) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      let res;
      try {
        res = await fetch(url, {
          headers: { 'Accept': 'application/json' },
          signal: controller.signal
        });
      } finally {
        clearTimeout(timeoutId);
      }
      if (!res.ok) return false;

      const data = await res.json();
      if (!Array.isArray(data?.aircraft)) return false;

      const now = Date.now();
      const cutoff = now - this.historyDurationMs;
      let seeded = 0;

      for (const ac of data.aircraft) {
        if (!ac?.hex || !Array.isArray(ac.points)) continue;

        const history = ac.points
          .filter((p) => Array.isArray(p) && p.length >= 4 && p[0] >= cutoff)
          .map(([ts, lat, lon, alt]) => ({ lat, lon, altitude: alt, timestamp: ts }))
          .sort((a, b) => a.timestamp - b.timestamp);
        if (history.length === 0) continue;

        const existing = this.tracked.get(ac.hex);
        if (existing) {
          // Live-Poll war schneller: nur ältere Punkte vorne anfügen
          const oldest = existing.history.length ? existing.history[0].timestamp : Infinity;
          const older = history.filter((p) => p.timestamp < oldest);
          if (older.length) {
            existing.history.unshift(...older);
            seeded++;
          }
          continue;
        }

        // Kurs/Geschwindigkeit aus den letzten beiden Punkten schätzen,
        // damit das Icon bis zum ersten Live-Poll richtig ausgerichtet ist
        const last = history[history.length - 1];
        const prev = history.length > 1 ? history[history.length - 2] : last;
        const dtSec = Math.max((last.timestamp - prev.timestamp) / 1000, 1);
        const distM = roughDistanceMeters(prev, last);

        this.tracked.set(ac.hex, {
          flight: {
            id: ac.hex,
            callsign: ac.callsign || ac.hex,
            position: {
              lat: last.lat,
              lon: last.lon,
              altitude: last.altitude,
              heading: roughHeading(prev, last)
            },
            velocity: distM / dtSec,
            verticalRate: 0,
            onGround: false,
            timestamp: last.timestamp,
            source: 'history',
            registration: ac.r ?? null,
            aircraftType: ac.t ?? null,
            category: ac.category ?? null,
            phase: 'cruising',
            squawk: null
          },
          history,
          lastSeen: last.timestamp
        });
        seeded++;
      }

      if (seeded > 0) {
        this._emitTracked(now);
        console.log(`History seeded: ${seeded} aircraft from server log`);
      }
      return seeded > 0;
    } catch (error) {
      console.log('No server-side history available:', error.message);
      return false;
    }
  }

  _startTimer() {
    this._clearTimer();
    this.timer = setInterval(() => this.poll(), this.pollIntervalMs);
  }

  _clearTimer() {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  _emitStatus(status) {
    const key = `${status.state}:${status.provider ?? ''}`;
    if (this.lastStatus !== key) {
      this.lastStatus = key;
      this.emit('status', status);
    }
  }

  async poll() {
    if (this.polling) return;
    const now = Date.now();
    if (now - this.lastFetchTime < this.minFetchGapMs) return;
    this.polling = true;
    this.lastFetchTime = now;

    const area = { lat: this.center.lat, lon: this.center.lon, radiusKm: this.radiusKm };

    try {
      for (let i = 0; i < PROVIDERS.length; i++) {
        const provider = PROVIDERS[i];
        try {
          const flights = await fetchFromProvider(provider, area);
          this.consecutiveErrors = 0;
          this._applyUpdate(flights);
          this._emitStatus({
            state: i === 0 ? 'live' : 'fallback',
            provider: provider.name,
            index: i,
            total: PROVIDERS.length,
            error: null
          });
          return;
        } catch (error) {
          console.warn(`Provider ${provider.name} failed:`, error.message);
        }
      }

      // All providers failed this cycle
      this.consecutiveErrors++;
      if (this.consecutiveErrors >= 2) {
        const bbox = radiusToBoundingBox(this.center, this.radiusKm);
        this._applyUpdate(generateDemoFlights(bbox), { demo: true });
        this._emitStatus({ state: 'demo', provider: null, error: 'All data providers unreachable' });
      } else {
        this._emitStatus({ state: 'error', provider: null, error: 'Data providers unreachable' });
      }
    } finally {
      this.polling = false;
    }
  }

  /**
   * Merge a fresh provider response into tracked state:
   * update history, flag briefly-missing flights as gone (kept for the
   * timeline), drop them once their history has aged out completely.
   */
  _applyUpdate(flights, { demo = false } = {}) {
    const now = Date.now();

    if (demo) {
      // Demo flights replace everything; keep their history growing too
      for (const [id] of this.tracked) {
        if (!id.startsWith('demo')) this.tracked.delete(id);
      }
    }

    for (const flight of flights) {
      let entry = this.tracked.get(flight.id);
      if (!entry) {
        entry = { flight, history: [], lastSeen: now };
        this.tracked.set(flight.id, entry);
      }
      entry.flight = flight;
      entry.lastSeen = now;
      entry.history.push({
        lat: flight.position.lat,
        lon: flight.position.lon,
        altitude: flight.position.altitude,
        timestamp: now
      });
    }

    this._emitTracked(now);
  }

  /**
   * Trim histories, apply gone/expiry lifecycle and emit the update event.
   */
  _emitTracked(now) {
    const historyCutoff = now - this.historyDurationMs;
    const result = [];

    for (const [id, entry] of this.tracked) {
      // History auf das Zeitfenster stutzen
      while (entry.history.length > 0 && entry.history[0].timestamp < historyCutoff) {
        entry.history.shift();
      }

      // Komplett veraltet (auch für die Zeitleiste irrelevant) → entfernen
      const newestTs = entry.history.length
        ? entry.history[entry.history.length - 1].timestamp
        : entry.lastSeen;
      if (newestTs < historyCutoff || entry.history.length === 0) {
        this.tracked.delete(id);
        continue;
      }

      // Kürzlich verschwunden → als "gone" markieren, History behalten
      entry.flight.gone = now - entry.lastSeen > this.staleAfterMs;
      entry.flight.history = entry.history;

      // Start/Landung am Heimatflughafen (asynchron aufgelöste Route)
      const csKey = (entry.flight.callsign || '').trim().toUpperCase();
      entry.flight.strTag = this.routeTags.get(csKey) ?? entry.flight.strTag ?? null;

      result.push(entry.flight);
    }

    this.emit('update', result);
    this._scheduleRouteLookups(result);
  }

  /**
   * Routen für noch nicht geprüfte Callsigns im Hintergrund auflösen
   * (max. 4 neue Anfragen pro Poll — adsb.lol drosselt sonst auch die
   * Positionsabfragen; Fehlversuche frühestens nach 2 min erneut).
   * Wird eine Route mit Heimatflughafen-Bezug gefunden, bekommt der Flug
   * sein strTag und ein Update wird emittiert.
   */
  _scheduleRouteLookups(flights) {
    if (!this.airport) return;
    const now = Date.now();
    let budget = 4;

    for (const flight of flights) {
      if (budget <= 0) break;
      if (flight.gone || flight.source === 'demo') continue;

      const key = (flight.callsign || '').trim().toUpperCase();
      if (!key || key.length < 4) continue;
      if (this.routeTags.has(key)) continue;
      const checked = this._routeChecked.get(key);
      if (checked && now - checked < 120000) continue;

      this._routeChecked.set(key, now);
      budget--;

      fetchRoute(key).then((route) => {
        const tag = this._strTagFor(route);
        if (tag) {
          this.routeTags.set(key, tag);
          this._emitTracked(Date.now());
        }
      });
    }
  }

  /**
   * 'departure' | 'arrival' | null für eine aufgelöste Route relativ zum
   * Heimatflughafen (IATA- und ICAO-Code werden akzeptiert).
   */
  _strTagFor(route) {
    if (!route) return null;
    const codes = new Set(
      [this.airport.code, this.airport.icao]
        .filter(Boolean)
        .map((c) => c.toUpperCase())
    );
    const matches = (ap) => Boolean(ap && (
      codes.has((ap.iata || '').toUpperCase()) ||
      codes.has((ap.icao || '').toUpperCase())
    ));
    const dep = matches(route.origin);
    const arr = matches(route.destination);
    if (dep && !arr) return 'departure';
    if (arr && !dep) return 'arrival';
    return null;
  }
}

/** Grobe Distanz in Metern (kleine Abstände, äquirektangular). */
function roughDistanceMeters(a, b) {
  const kmPerDegLat = 111.32;
  const dLat = (b.lat - a.lat) * kmPerDegLat;
  const dLon = (b.lon - a.lon) * kmPerDegLat * Math.cos(((a.lat + b.lat) / 2) * Math.PI / 180);
  return Math.sqrt(dLat * dLat + dLon * dLon) * 1000;
}

/** Grober Kurs (Grad, 0 = Nord) zwischen zwei Punkten. */
function roughHeading(a, b) {
  const dLon = (b.lon - a.lon) * Math.cos(((a.lat + b.lat) / 2) * Math.PI / 180);
  const dLat = b.lat - a.lat;
  if (dLon === 0 && dLat === 0) return 0;
  let deg = Math.atan2(dLon, dLat) * 180 / Math.PI;
  if (deg < 0) deg += 360;
  return deg;
}
